/**
 * Montagem das 5 alternativas.
 *
 * Centralizado de proposito: se cada template ordenasse as suas, as regras de
 * ordenacao e de posicao da chave seriam reimplementadas 85 vezes, com 85
 * oportunidades de errar.
 *
 * Nota sobre a posicao da chave: para alternativas NUMERICAS o catalogo manda ordenar
 * em ordem crescente, o que significa que a posicao da chave e determinada pelo VALOR,
 * nao por sorteio. Isso distribui as posicoes naturalmente ao longo do banco -- e a
 * bateria de testes verifica em agregado que a distribuicao nao degenerou (a chave nao
 * pode ser sistematicamente o maior ou o menor).
 */

import type { Rng } from '../core/rng.ts';
import type { Alternative, ResolvedDistractor, SelectOneInteraction } from '../schema/item.ts';
import type { RichText } from '../schema/richtext.ts';
import type { AltStructure, ChoiceId } from '../schema/taxonomy.ts';
import { CHOICE_IDS } from '../schema/taxonomy.ts';
import type { Scene } from '../render/scene.ts';

export interface AssembleOptions<A> {
  readonly structure: AltStructure;
  readonly key: A;
  readonly distractors: readonly ResolvedDistractor<A>[];
  /** Como cada valor vira texto da alternativa. */
  readonly render: (value: A) => RichText;
  /** Valor bruto guardado para invariantes e analitica. */
  readonly raw?: (value: A) => number | string | null;
  /** Figura da alternativa, quando a estrutura e E6. */
  readonly figure?: (value: A) => Scene;
  readonly rng: Rng;
}

export function assembleSelectOne<A>(opts: AssembleOptions<A>): SelectOneInteraction {
  const raw = opts.raw ?? ((v: A) => (typeof v === 'number' || typeof v === 'string' ? v : null));

  type Entry = {
    value: A;
    error: Alternative['error'];
    rawValue: number | string | null;
    explanation?: Alternative['explanation'];
  };

  const entries: Entry[] = [
    { value: opts.key, error: null, rawValue: raw(opts.key) },
    ...opts.distractors.map((d) => {
      const e: Entry = { value: d.value, error: d.error, rawValue: raw(d.value) };
      if (d.explanation) e.explanation = d.explanation;
      return e;
    }),
  ];

  const todasNumericas = entries.every((e) => typeof e.rawValue === 'number');

  const ordenadas = todasNumericas
    ? [...entries].sort((a, b) => (a.rawValue as number) - (b.rawValue as number))
    : opts.rng.fork('ordem-alternativas').shuffle(entries);

  const alternatives = ordenadas.map((e, i) => {
    const id = CHOICE_IDS[i] as ChoiceId;
    const base: Alternative = {
      id,
      content: opts.render(e.value),
      error: e.error,
      rawValue: e.rawValue,
      ...(e.explanation ? { explanation: e.explanation } : {}),
    };
    return opts.figure ? { ...base, figure: opts.figure(e.value) } : base;
  }) as unknown as SelectOneInteraction['alternatives'];

  const keyIndex = ordenadas.findIndex((e) => e.error === null);

  return {
    kind: 'select-one',
    structure: opts.structure,
    alternatives,
    keyId: CHOICE_IDS[keyIndex] as ChoiceId,
  };
}
